'use client';

import { useIntl } from 'react-intl';
import { InlineNotification, Notification } from '@packages/shared';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import type { FragmentType } from '@packages/gql/generated/shopping/fragment-masking';
import { getFragmentData } from '@packages/gql/generated/shopping/fragment-masking';
import { useState } from 'react';
import { AddRatingFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddRatingFragmentFragmentDoc';
import { useRatingTracking } from '../../Tracking/hooks/useRatingTracking';
import { AddRatingDialog } from './AddRatingDialog';
import { ratingMessages } from './messages';
import { CompactProduct } from '../../CompactProduct/CompactProduct';
import { useWriteCustomerRatingEnabled } from '../useWriteCustomerRatingEnabled';
import { AddRatingCallToAction } from '../../CustomerRatingsV2/AddRatingCallToAction/AddRatingCallToAction';

/* GraphQL */ `
  fragment AddRatingFragment on Product {
    ...MultiStepFormFragment
    ...CompactProductFragment
  }
`;

type AddRatingProps = {
  /**
   * ProductData for glycerin submitRating event
   */
  productData: FragmentType<typeof AddRatingFragmentFragmentDoc>;
};

/**
 * Add rating dashed button with multi step form in a dialog
 */
export const AddRating = ({ productData }: AddRatingProps) => {
  const product = getFragmentData(AddRatingFragmentFragmentDoc, productData);
  const [dialogOpened, setDialogOpened] = useState(false);
  const [openSuccessNotification, setOpenSuccessNotification] = useState(false);
  const [openErrorNotification, setOpenErrorNotification] = useState(false);
  const { isMobile } = useDeviceType();
  const { formatMessage } = useIntl();

  const { trackGlycerinButtonClick } = useRatingTracking();
  const writeCustomerRatingEnabled = useWriteCustomerRatingEnabled();

  if (!writeCustomerRatingEnabled) return null;

  const handleSuccess = () => {
    setOpenSuccessNotification(true);
    setDialogOpened(false);
  };

  const handleOpenDialog = ({ category }: { category: string }) => {
    trackGlycerinButtonClick({ category });
    setDialogOpened(true);
  };

  return (
    <>
      <Notification
        open={openSuccessNotification}
        severity="success"
        autoHideDuration={5000}
        onClose={() => setOpenSuccessNotification(false)}
      >
        {formatMessage(ratingMessages.newRatingSubmitSuccessNotification)}
      </Notification>
      <Notification
        open={openErrorNotification}
        severity="error"
        autoHideDuration={5000}
        onClose={() => setOpenErrorNotification(false)}
      >
        {formatMessage(ratingMessages.newRatingSubmitErrorNotification)}
      </Notification>
      {dialogOpened && (
        <AddRatingDialog
          open={dialogOpened}
          title={formatMessage(ratingMessages.dialogTitle)}
          handleClose={() => setDialogOpened(false)}
          contentProps={{
            productData: product,
            productOverview: <CompactProduct data={product} withProductName showTaxHint={false} />,
            onSuccess: handleSuccess,
            onError: () => setOpenErrorNotification(true),
          }}
        />
      )}
      {openSuccessNotification && isMobile && (
        <InlineNotification severity="info">
          {formatMessage(ratingMessages.newRatingSubmitSuccess)}
        </InlineNotification>
      )}
      <AddRatingCallToAction
        onAction={() => handleOpenDialog({ category: ratingMessages.buttonLabel.defaultMessage })}
      />
    </>
  );
};
